import { createFileRoute, Link, notFound, redirect, useRouter } from "@tanstack/react-router";
import { analyzeSources } from "@/lib/source-tiers";
import { MERGED_DISCOVERIES } from "@/lib/merged-discoveries";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocale } from "@/hooks/use-locale";
import { getDiscoveryTranslation, LANG_LABELS } from "@/lib/translations.functions";
import { ARCHETYPES } from "@/lib/archetypes";
import { ArrowLeft, ArrowRight, CheckCircle2, PlayCircle, Sparkles } from "lucide-react";
import { classifyVibe } from "@/lib/feed-engine";
import { SiteHeader } from "@/components/SiteHeader";
import { BackButton } from "@/components/BackButton";
import { SiteFooter } from "@/components/SiteFooter";
import { DiscoveryComments } from "@/components/DiscoveryComments";
import {
  getDiscoveryBySlug,
  getTrendingDiscoveries,
  logDiscoveryView,
  type DBCategory,
  type DBDiscovery,
  type DBQuiz,
  type TrendingItem,
} from "@/lib/content.functions";
import { absoluteImage, isUnsafeImageReference, sizedImage } from "@/lib/asset-map";
import { ShareButtons } from "@/components/ShareButtons";
import { Reactions } from "@/components/Reactions";
import { ShareToStory } from "@/components/ShareToStory";
import { ContextualProductCTA } from "@/components/ContextualProductCTA";

import { ReadingProgress } from "@/components/ReadingProgress";
import { SaveButton } from "@/components/SaveButton";
import { InstagramBridge } from "@/components/discovery/InstagramBridge";
import { Breadcrumbs, type Crumb } from "@/components/Breadcrumbs";
import { ArticleBody } from "@/components/ArticleBody";
import { faqsForSlug, type Faq } from "@/lib/article-faqs";
import { AnalystCall } from "@/components/discovery/AnalystCall";
import { CallRecordBanner } from "@/components/discovery/CuriosityFooter";
import { CertifyDiscovery } from "@/components/discovery/CertifyDiscovery";
import { FastFacts } from "@/components/discovery/FastFacts";
import { LanguageFeedbackBanner } from "@/components/LanguageFeedbackBanner";

import { useGuessStreak } from "@/hooks/use-guess-streak";

import { AutoNextCountdown } from "@/components/AutoNextCountdown";
import { CinematicHero } from "@/components/cinematic/CinematicHero";
import { StickyNextBar } from "@/components/StickyNextBar";

import { useCollectionProgress } from "@/hooks/use-collection-progress";
import { getJourney } from "@/lib/journeys";
import { useSessionVisited, pickUnseen, readSessionVisited } from "@/lib/session-visited";

import { useReadProgress } from "@/hooks/use-read-progress";
import { usePageView } from "@/hooks/use-page-view";
import { trackConversion } from "@/hooks/use-page-view";
import { freeTranslate, normalizeSource } from "@/lib/free-translate";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";
import { validVisualAssets } from '@/lib/visual-assets';

type CollectionContext = {
  collection: {
    id: string;
    slug: string;
    title: string;
    description: string | null;
    cover_url: string | null;
  };
  items: Array<{ id: string; slug: string; title: string; image_url: string | null }>;
  currentIndex: number;
};

type DiscoveryRouteData = {
  discovery: DBDiscovery;
  category: DBCategory | null;
  related: DBDiscovery[];
  crossCategory: DBDiscovery[];
  categories: DBCategory[];
  trending: TrendingItem[];
  prev: Pick<DBDiscovery, "id" | "slug" | "title" | "image_url" | "excerpt" | "sort_order"> | null;
  next: Pick<DBDiscovery, "id" | "slug" | "title" | "image_url" | "excerpt" | "sort_order"> | null;
  collectionContext: CollectionContext | null;
  quiz: DBQuiz | null;
  originReel: {
    permalink: string | null;
    views: number | null;
    likes: number | null;
    ext_id: string | null;
  } | null;
  initialTranslation: Awaited<ReturnType<typeof getDiscoveryTranslation>> | null;
};

const VIBES: Record<string, { emoji: string; label: string }> = {
  cosmos: { emoji: "🌌", label: "Cosmic" },
  space: { emoji: "🌌", label: "Cosmic" },
  animals: { emoji: "🐙", label: "Wild" },
  "weird-history": { emoji: "🤯", label: "Bizarre" },
  "human-body": { emoji: "🧠", label: "Mind-bending" },
  "physics-weirdness": { emoji: "⚛️", label: "Reality-breaking" },
  "weird-science": { emoji: "🧪", label: "Holy shit" },
  earth: { emoji: "🌋", label: "Earth-shaking" },
  technology: { emoji: "🤖", label: "Future-bending" },
};

export const Route = createFileRoute("/discovery/$slug")({
  validateSearch: (search: Record<string, unknown>): { from?: string; ref?: string; lang?: keyof typeof LANG_LABELS } => {
    const from = typeof search.from === "string" ? search.from : undefined;
    const ref = typeof search.ref === "string" ? search.ref : undefined;
    const lang = typeof search.lang === "string" && search.lang in LANG_LABELS
      ? search.lang as keyof typeof LANG_LABELS
      : undefined;
    return { ...(from ? { from } : {}), ...(ref ? { ref } : {}), ...(lang ? { lang } : {}) };
  },

  loaderDeps: ({ search }) => ({ from: search.from, lang: search.lang }),
  loader: async ({ params, deps, context }) => {
    const [data, trending] = await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["discovery", params.slug, deps.from ?? ""],
        queryFn: () =>
          getDiscoveryBySlug({ data: { slug: params.slug, collectionSlug: deps.from } }),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["trending", params.slug],
        queryFn: () => getTrendingDiscoveries({ data: { limit: 5, excludeSlug: params.slug } }),
      }),
    ]);
    if (!data) {
      // Merged/retired articles keep their Google ranking - send it to the
      // surviving article instead of returning a 404.
      const merged = MERGED_DISCOVERIES[params.slug];
      if (merged) {
        throw redirect({ to: "/discovery/$slug", params: { slug: merged }, statusCode: 301 });
      }
      throw notFound();
    }
    const initialTranslation = deps.lang && deps.lang !== "en"
      ? await getDiscoveryTranslation({ data: { slug: params.slug, lang: deps.lang, cacheOnly: true } })
      : null;
    return { ...data, trending: trending.items, initialTranslation };
  },

  head: ({ params, loaderData }) => {
    const raw = loaderData?.discovery;
    const localized = loaderData?.initialTranslation;
    const d = raw && localized
      ? { ...raw, title: localized.title ?? raw.title, excerpt: localized.excerpt ?? raw.excerpt, body: localized.body ?? raw.body }
      : raw;
    const cat = loaderData?.category;
    // SEO overrides win when present; otherwise fall back to article fields.
    const seoTitle = d?.seo_title?.trim() ? d.seo_title.trim() : d?.title;
    const title = seoTitle ? `${seoTitle} · Smartasaurus` : "Discovery · Smartasaurus";
    const desc = d?.seo_description?.trim()
      ? d.seo_description.trim()
      : (d?.excerpt ??
        "A fact-checked discovery from Smartasaurus - animals, deep ocean, your own body, and how the world really works.");
    const absImg = absoluteImage(d?.image_url);
    const url = `https://smartasaurus.com/discovery/${params.slug}`;
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:site_name", content: "Smartasaurus" },
      { name: "twitter:card", content: absImg ? "summary_large_image" : "summary" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
    ];
    // Pinterest Rich Pins (Article type) read these open-graph article:* tags.
    if (d?.created_at) {
      meta.push({
        property: "article:published_time",
        content: new Date(d.created_at).toISOString(),
      });
      meta.push({
        property: "article:modified_time",
        content: new Date(d.created_at).toISOString(),
      });
    }
    meta.push({ property: "article:author", content: "Smartasaurus" });
    meta.push({ name: "author", content: "Smartasaurus" });
    if (cat?.label) meta.push({ property: "article:section", content: cat.label });
    if (absImg) {
      meta.push({ property: "og:image", content: absImg });
      meta.push({ property: "og:image:alt", content: d?.title ?? "Smartasaurus" });
      meta.push({ property: "og:image:width", content: "1200" });
      meta.push({ property: "og:image:height", content: "630" });
      meta.push({ name: "twitter:image", content: absImg });
    }

    const scripts = d
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: d.title,
              description: d.excerpt,
              image: absImg ? [absImg] : undefined,
              mainEntityOfPage: { "@type": "WebPage", "@id": url },
              url,
              author: {
                "@type": "Organization",
                name: "Smartasaurus",
                url: "https://smartasaurus.com",
              },
              publisher: {
                "@type": "Organization",
                name: "Smartasaurus",
                url: "https://smartasaurus.com",
              },
              datePublished: d.created_at,
              dateModified: d.created_at,
              timeRequired: `PT${Math.max(1, Math.round(((d.body ?? "").split(/\s+/).filter(Boolean).length + (d.excerpt ?? "").split(/\s+/).filter(Boolean).length) / 200))}M`,
              articleSection: cat?.label ?? undefined,
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://smartasaurus.com/",
                },
                ...(cat
                  ? [
                      {
                        "@type": "ListItem",
                        position: 2,
                        name: cat.label,
                        item: `https://smartasaurus.com/category/${cat.slug}`,
                      },
                    ]
                  : []),
                { "@type": "ListItem", position: cat ? 3 : 2, name: d.title, item: url },
              ],
            }),
          },
        ]
      : undefined;
    // FAQ structured data - only for pages with hand-written Q&A mirroring
    // real Search Console queries. The same array renders visibly below.
    const faqs = d ? faqsForSlug(d.slug) : [];
    if (scripts && faqs.length > 0) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f: Faq) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      });
    }
    // Preload first two documentary-visual frames so playback starts instantly.
    const gallery: Array<{ url: string }> = Array.isArray((d as any)?.image_gallery)
      ? (d as any).image_gallery
      : [];

    const preloadLinks = gallery.slice(0, 2).map((g) => ({
      rel: "preload",
      as: "image",
      href: g.url,
      fetchPriority: "high" as const,
    }));
    // No ?lang= hreflang alternates: translation is client-side, so those URLs
    // serve identical English HTML. Declaring them as language versions made
    // Google index ?lang=fr / ?lang=pt duplicates that competed with the clean
    // URL and split its ranking signal.
    return { meta, links: [{ rel: "canonical", href: url }, ...preloadLinks], scripts };
  },
  component: DiscoveryPage,
  errorComponent: ({ reset }) => {
    const router = useRouter();
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
        <div>
          <p className="text-muted-foreground">Couldn't load this discovery.</p>
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="mt-4 rounded-full bg-primary px-4 py-2 text-sm font-bold uppercase text-primary-foreground"
          >
            Retry
          </button>
        </div>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
      <div>
        <h1 className="font-display text-6xl">404</h1>
        <p className="mt-2 text-muted-foreground">This discovery doesn't exist.</p>
        <Link
          to="/"
          className="mt-4 inline-block rounded-full bg-primary px-4 py-2 text-sm font-bold uppercase text-primary-foreground"
        >
          Back home
        </Link>
      </div>
    </div>
  ),
});

function readingTime(body?: string | null, excerpt?: string) {
  const text = `${body ?? ""} ${excerpt ?? ""}`.trim();
  const words = text ? text.split(/\s+/).length : 0;
  return Math.max(1, Math.round(words / 200));
}

function ConfidenceBadge({ level }: { level: "high" | "medium" | "speculative" }) {
  const map = {
    high: {
      label: "High confidence",
      cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    },
    medium: {
      label: "Medium confidence",
      cls: "border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400",
    },
    speculative: {
      label: "Speculative",
      cls: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
    },
  } as const;
  const v = map[level];
  return (
    <>
      <span>·</span>
      <span
        title="Editorial confidence level - see Sources at the end of the article."
        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${v.cls}`}
      >
        {v.label}
      </span>
    </>
  );
}

function DiscoveryPage() {
  const data = Route.useLoaderData() as DiscoveryRouteData;
  const {
    discovery: discoveryRaw,
    category,
    related,
    crossCategory,
    categories,
    trending,
    collectionContext,
    quiz,
    originReel,
  } = data;
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const queryClient = useQueryClient();
  const translationStorageKey = `sa.article-translation.v1.${discoveryRaw.slug}.${lang}`;
  const storedTranslation = useMemo(() => {
    if (lang === "en" || typeof window === "undefined") return undefined;
    try {
      return JSON.parse(window.localStorage.getItem(translationStorageKey) ?? "null") ?? undefined;
    } catch {
      return undefined;
    }
  }, [lang, translationStorageKey]);
  const { data: translation, isFetching: translationLoading } = useQuery({
    queryKey: ["discovery-translation", discoveryRaw.slug, lang],
    queryFn: async () => {
      const approved = await getDiscoveryTranslation({ data: { slug: discoveryRaw.slug, lang } });
      if (approved || lang === "en") return approved;

      // The article owns the free browser allowance. Translating it here,
      // before the page-wide interface pass, prevents labels and footer copy
      // from exhausting the free tier while the actual story stays English.
      const sources = [
        discoveryRaw.title,
        discoveryRaw.excerpt ?? "",
        discoveryRaw.body ?? "",
      ].filter(Boolean);
      const free = await freeTranslate(lang, sources, { sourceIsEnglish: true });
      const title = free.get(normalizeSource(discoveryRaw.title));
      const excerpt = discoveryRaw.excerpt
        ? free.get(normalizeSource(discoveryRaw.excerpt))
        : undefined;
      const body = discoveryRaw.body ? free.get(normalizeSource(discoveryRaw.body)) : undefined;
      if (!title || (discoveryRaw.excerpt && !excerpt) || (discoveryRaw.body && !body)) return null;
      return { lang, title, excerpt, body };
    },
    enabled: lang !== "en",
    // Only seed with a translation for THIS language. The loader's copy belongs
    // to the language in the URL at load time; reusing it after a mid-page
    // switch kept captions/body in the previous language and even cached it
    // under the new language's storage key.
    initialData: () => {
      const loaderCopy = data.initialTranslation as { lang?: string } | null | undefined;
      if (loaderCopy && loaderCopy.lang === lang) return data.initialTranslation ?? undefined;
      const stored = storedTranslation as { lang?: string } | undefined;
      return stored && (!stored.lang || stored.lang === lang) ? storedTranslation : undefined;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    // If another reader's translation is in progress, check back briefly.
    refetchInterval: (q) =>
      q.state.data || q.state.dataUpdateCount > 4 ? false : 30_000,
  });
  useEffect(() => {
    if (lang === "en" || !translation) return;
    try {
      window.localStorage.setItem(translationStorageKey, JSON.stringify(translation));
    } catch {
      /* optional cache */
    }
  }, [lang, translation, translationStorageKey]);
  const discovery = useMemo(() => {
    if (lang === "en" || !translation) return discoveryRaw;
    return {
      ...discoveryRaw,
      title: translation.title ?? discoveryRaw.title,
      excerpt: translation.excerpt ?? discoveryRaw.excerpt,
      body: translation.body ?? discoveryRaw.body,
      audio_script: (translation as any).audio_script ?? discoveryRaw.audio_script,
    };
  }, [discoveryRaw, translation, lang]);
  // Articles always render. Without a reviewed translation the page translator
  // handles the text; an article must never be hidden behind a notice.
  const awaitingTranslation = false as boolean;
  const img = sizedImage(discovery.image_url, 1280);
  const safeGallery = useMemo(() => {
    const stored = (discovery as any).image_gallery;
    const raw = Array.isArray(stored) ? stored : Array.isArray(stored?.items) ? stored.items : [];
    const safe = raw
      .map((g: any) => ({ ...g, url: typeof g?.url === "string" ? g.url : "" }))
      .filter(
        (g: any) =>
          g.url &&
          !isUnsafeImageReference(
            g.url,
            `${g.sourceTitle ?? ""} ${g.concept ?? ""} ${g.beat ?? ""}`,
          ),
      );
    // Never present a failed one-frame gallery as a slideshow. The static cover
    // remains visible until enough verified frames exist for real motion.
    return safe.length >= 2 ? safe : [];
  }, [discovery]);

  const url = `https://smartasaurus.com/discovery/${discovery.slug}`;

  // Progress tracking for the active collection (client-only, localStorage).
  const collSlug = collectionContext?.collection.slug ?? null;
  const { visited, markVisited } = useCollectionProgress(collSlug);

  // Session-scoped seen set - shared by every "next" surface so we never
  // ping-pong the reader between the same two articles.
  const sessionVisited = useSessionVisited(discovery.slug);

  // Collection-aware Up Next within an active series.
  // Skip forward past episodes already read this session (loop guard), but
  // never wrap backwards - the series still ends when it ends.
  const collNext = useMemo(() => {
    if (!collectionContext || collectionContext.currentIndex < 0) return null;
    const rest = collectionContext.items.slice(collectionContext.currentIndex + 1);
    return (
      rest.find((i) => !sessionVisited.has(i.slug) && i.slug !== discovery.slug) ?? rest[0] ?? null
    );
  }, [collectionContext, sessionVisited, discovery.slug]);

  // Warm only already-cached translations for the next episode. This removes
  // navigation latency without generating speculative paid translations.
  useEffect(() => {
    if (lang === "en" || !collNext) return;
    void queryClient.prefetchQuery({
      queryKey: ["discovery-translation", collNext.slug, lang],
      queryFn: () =>
        getDiscoveryTranslation({ data: { slug: collNext.slug, lang, cacheOnly: true } }),
      staleTime: Infinity,
    });
  }, [collNext, lang, queryClient]);

  // Scroll-depth analytics (Plausible custom events).
  // One page_view per article open per session - makes article traffic visible in the dashboard
  // alongside discovery_views (which powers "Article Reads").
  usePageView("article");

  // Scroll-depth analytics (Plausible custom events).
  useReadProgress(discovery.slug, {
    category: category?.slug ?? "uncategorized",
    collection: collectionContext?.collection.slug ?? "none",
  });

  // Record one view per session per article (fire-and-forget).
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Mark this discovery as visited within its collection (if any).
    markVisited(discovery.slug);
    // Record local reading history for "Continue Exploring".
    import("@/lib/history").then(({ recordHistory }) =>
      recordHistory(discovery.slug, discovery.category_id ?? null),
    );
    const key = `sa.view.${discovery.slug}`;
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, "1");
    let sessionId = window.localStorage.getItem("sa.session");
    if (!sessionId) {
      sessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      try {
        window.localStorage.setItem("sa.session", sessionId);
      } catch {
        /* ignore */
      }
    }
    // 1.5s dwell threshold filters bounce/preload pings.
    const t = window.setTimeout(() => {
      logDiscoveryView({ data: { slug: discovery.slug, sessionId: sessionId! } }).catch(() => {});
      // Record signed-in user reading history (best-effort, no-op when signed out).
      import("@/integrations/supabase/client").then(({ supabase }) =>
        supabase.auth.getSession().then(({ data: s }) => {
          if (!s.session) return;
          import("@/lib/account.functions").then(({ recordDiscoveryView }) =>
            recordDiscoveryView({ data: { discoveryId: discovery.id, progress: 25 } }).catch(
              () => {},
            ),
          );
        }),
      );
    }, 1500);
    return () => window.clearTimeout(t);
  }, [discovery.slug, discovery.id, markVisited]);

  // "Call it" belief gate - the reader commits before the payoff.
  const streak = useGuessStreak(discovery.slug);
  const [justAnswered, setJustAnswered] = useState<"real" | "fake" | undefined>(undefined);
  // Translated narration takes over the soundtrack while it is active.
  const [foreignActive, setForeignActive] = useState(false);
  const [foreignPlaying, setForeignPlaying] = useState(false);
  const [foreignProgress, setForeignProgress] = useState({ current: 0, duration: 0 });
  const answered = justAnswered ?? streak.answeredHere;
  // Locked only once we know (post-hydration) they haven't answered yet.
  const locked = streak.hydrated && !answered;

  return (
    <div className="min-h-screen bg-background">
      <ReadingProgress />
      <SiteHeader />
      <main id="main-content">
        {collectionContext && collectionContext.currentIndex >= 0 && (
          <CollectionStrip ctx={collectionContext} visited={visited} />
        )}
        <article className="mx-auto max-w-4xl px-4 pt-6 pb-8 md:px-8 md:pt-8 lg:max-w-6xl">
          <div className="mb-4 flex items-center gap-3">
            {Route.useSearch().ref === "feed" ? (
              <Link
                to="/discoveries"
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/50 bg-primary/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-primary transition hover:bg-primary/20"
              >
                ← {ui("Back to articles")}
              </Link>
            ) : (
              <BackButton fallback={{ to: "/discoveries" }} />
            )}

            <div className={category ? "hidden md:block" : undefined}>
              <Breadcrumbs
                items={
                  [
                    ...(category
                      ? [
                          {
                            label: category.label,
                            to: "/category/$slug" as const,
                            params: { slug: category.slug },
                          },
                        ]
                      : []),
                    { label: discovery.title, current: true as const },
                  ] as Crumb[]
                }
              />
            </div>
          </div>

          {/* Title block - ABOVE the cinematic hero so users always see what
              the article is about before the video takes over the screen. */}
          {/* No "not translated" banner: the page translator covers the text in the visitor's browser. */}
          {awaitingTranslation && translationLoading && (
            <div className="mb-4 rounded-xl border border-border bg-card/60 px-4 py-3 text-sm text-muted-foreground" role="status">
              <span data-no-translate>{curatedUiTranslation(lang, "Loading the selected language...") ?? "Loading the selected language..."}</span>
            </div>
          )}

          {awaitingTranslation && !translationLoading && (
            <div className="mb-4 rounded-xl border border-border bg-card/60 px-4 py-3 text-sm text-muted-foreground" role="alert" data-no-translate>
              {({
                fr: "Cette découverte n’est pas encore disponible en français.",
                es: "Este descubrimiento todavía no está disponible en español.",
                pt: "Esta descoberta ainda não está disponível em português.",
                de: "Diese Entdeckung ist noch nicht auf Deutsch verfügbar.",
                sv: "Den här upptäckten finns ännu inte på svenska.",
                hi: "यह खोज अभी हिन्दी में उपलब्ध नहीं है।",
                zh: "这项发现暂时还没有中文版。",
                ru: "Это открытие пока недоступно на русском языке.",
                ar: "هذا الاكتشاف غير متاح بالعربية بعد.",
              } as Record<string, string>)[lang]}
            </div>
          )}

          {!awaitingTranslation && <header className="mb-4">
            {category && (
              <Link
                to="/category/$slug"
                params={{ slug: category.slug }}
                className="inline-block rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.3em] text-primary transition hover:border-primary hover:bg-primary/20"
              >
                {category.label}
              </Link>
            )}
            <h1 data-no-translate={translation ? "true" : undefined} className="mt-3 font-display text-2xl uppercase leading-[1.05] [text-wrap:balance] md:text-5xl lg:text-6xl">
              {discovery.title}
            </h1>
            <p data-no-translate={translation ? "true" : undefined} className="mt-2 text-sm text-muted-foreground md:text-xl">{discovery.excerpt}</p>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted-foreground">
              <span>{ui("By Smartasaurus")}</span>
              {discovery.body &&
                (() => {
                  const words = discovery.body.trim().split(/\s+/).length;
                  const mins = Math.max(1, Math.round(words / 220));
                  return (
                    <span className="inline-flex items-center gap-1">
                      · <span aria-label={ui(`${mins} min read`)}>{ui(`${mins} min read`)}</span>
                    </span>
                  );
                })()}
              {category &&
                (() => {
                  const vibe = VIBES[category.slug] ?? { emoji: "✨", label: "Curious" };
                  return (
                    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                      <span aria-hidden>{vibe.emoji}</span> {ui(vibe.label)}
                    </span>
                  );
                })()}
            </div>
          </header>}

          {awaitingTranslation ? null : <>

          {/* Discovery hero - silent storytelling video (or hero image fallback).
              The narration player below is the master controller. */}

          <div>
            <CinematicHero
              slug={discovery.slug}
              title={discovery.title}
              excerpt={discovery.excerpt}
              body={discovery.body}
              audioScript={discoveryRaw.audio_script ?? discoveryRaw.body ?? null}
              captionScript={
                lang === "en"
                  ? discoveryRaw.audio_script ?? discoveryRaw.body ?? null
                  : translation?.audio_script ?? translation?.body ?? null
              }
              heroImageUrl={img || null}
              midImageUrl={discovery.mid_image_url}
              imageGallery={safeGallery}
              categoryLabel={category?.label ?? null}
              categorySlug={category?.slug ?? null}
              initialAudioUrl={discovery.audio_url}
              silentNarration={foreignActive}
              externalPlaying={foreignActive ? foreignPlaying : undefined}
              externalCurrent={foreignActive ? foreignProgress.current : undefined}
              externalDuration={foreignActive ? foreignProgress.duration : undefined}
            />
          </div>

          {/* Ask the grounded question after the visitor has met the story. */}
          <div className="mt-5">
            <AnalystCall
              slug={discovery.slug}
              quiz={
                quiz
                  ? {
                      question: quiz.question,
                      options: quiz.options,
                      answer_index: quiz.answer_index,
                      explanation: quiz.explanation,
                    }
                  : null
              }
              answered={answered}
              onAnswer={(sharp) => {
                setJustAnswered(sharp ? "real" : "fake");
                streak.record(sharp ? "real" : "fake", sharp);
              }}
            />
          </div>

          {/* Action bar - the single sharing/saving surface, right under the
              video where attention peaks. Story-share is the primary button. */}
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card/60 p-3">
            <ShareToStory
              title={discovery.title}
              imageUrl={discovery.image_url}
              url={url}
              eyebrow={category?.label ?? null}
            />
            <ShareButtons url={url} title={discovery.title} />
            <SaveButton discoveryId={discovery.id} />
            <span className="ml-auto hidden text-[11px] text-muted-foreground sm:block">
              {ui("Send this to someone who’d love it")}
            </span>
          </div>

          {discovery.body && <FastFacts body={discovery.body} excerpt={discovery.excerpt} />}

          {/* Instagram bridge */}
          {originReel?.permalink && (
            <InstagramBridge permalink={originReel.permalink} views={originReel.views} />
          )}

          {/* Hero image is suppressed - the Motion Experience above is the
              article's visual introduction. Fallback only if there's no image. */}
          {!img && (
            <div className="mt-6 aspect-[16/9] w-full rounded-2xl border border-border bg-card" />
          )}

          {/* Reading region: body left, sticky rail right on lg+. */}
          <div className="mt-2 lg:grid lg:grid-cols-[1fr_300px] lg:gap-12">
            <div className="min-w-0">
              {/* Motion "Read the discovery" jumps here - first paragraph of the article. */}
              <div id="motion-handoff-target" aria-label="Article start" className="scroll-mt-24" />

              {/* Body */}
              <div className="justify-self-center">
                {discovery.body ? (
                  <ArticleBody
                    body={discovery.body}
                    midImageUrl={discovery.mid_image_url ?? null}
                    midImageAlt={discovery.title}
                    explore={
                      related[0]
                        ? {
                            href: { to: "/discovery/$slug", params: { slug: related[0].slug } },
                            label: category ? `${ui("More from")} ${category.label}` : ui("Keep exploring"),
                            title: related[0].title,
                          }
                        : category
                          ? {
                              href: { to: "/category/$slug", params: { slug: category.slug } },
                              label: ui("Explore the hub"),
                              title: `More ${category.label} discoveries`,
                            }
                          : undefined
                    }
                  />
                ) : (
                  <p className="mt-7 text-muted-foreground italic">Full story coming soon.</p>
                )}
              </div>

              <LanguageFeedbackBanner lang={lang} />

              {/* People also ask - real Google queries this page already appears
              for, answered directly. Matches the FAQPage structured data. */}
              {faqsForSlug(discovery.slug).length > 0 && (
                <section className="mt-8 rounded-2xl border border-border bg-card/40 p-5">
                  <h2 className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
                    {ui("Straight answers")}
                  </h2>
                  <dl className="mt-4 space-y-5">
                    {faqsForSlug(discovery.slug).map((f) => (
                      <div key={f.q}>
                        <dt className="font-semibold text-foreground">{f.q}</dt>
                        <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">
                          {f.a}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              )}

              {/* One community block: reaction tap first (zero friction), then comments. */}
              <section className="mt-10 rounded-2xl border border-border bg-card/40 p-5 print:hidden">
                <h2 className="font-display text-lg uppercase tracking-wide text-foreground">
                  {ui("How did this hit you?")}
                </h2>
                <Reactions slug={discovery.slug} />
                <div className="mt-6 border-t border-border pt-5">
                  <p className="text-sm text-muted-foreground">
                    {ui("Or say it in words - one line is enough.")}
                  </p>
                  <div className="mt-4">
                    <DiscoveryComments discoveryId={discovery.id} />
                  </div>
                </div>
              </section>

              {discovery.body && (
                <CertifyDiscovery
                  slug={discovery.slug}
                  title={discovery.title}
                  categorySlug={category?.slug ?? null}
                  categoryLabel={category?.label ?? null}
                />
              )}

              {/* Sources - collapsed by default; keeps EEAT signal without visual weight. */}
              {validVisualAssets((discovery as any).image_gallery).length > 0 && (
                <details className="mt-8 border-t border-border py-4">
                  <summary className="cursor-pointer text-sm text-muted-foreground">Image credits</summary>
                  <ol className="mt-3 space-y-3 text-xs text-muted-foreground">
                    {validVisualAssets((discovery as any).image_gallery).map((asset) => (
                      <li key={asset.assetKey} className="break-words">
                        <a href={asset.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline">{asset.sourceTitle}</a>
                        {' '}by {asset.creator}.{' '}
                        <a href={asset.licenseUrl} target="_blank" rel="noopener noreferrer" className="underline">{asset.license}</a>
                        {'. Presentation includes camera movement and transitions. '}{asset.restrictions}
                      </li>
                    ))}
                  </ol>
                </details>
              )}
              {discovery.sources && discovery.sources.length > 0 && (() => {
                // Evidence and discovery provenance are different things: a reel
                // is where the story came from, never what makes it true.
                const a = analyzeSources(discovery.sources);
                if (a.evidence.length === 0 && a.inspiredBy.length === 0) return null;
                return (
                  <details className="mt-8 rounded-2xl border border-border bg-card/40">
                    <summary className="cursor-pointer list-none px-5 py-4 text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground hover:text-foreground">
                      {ui(`Sources (${a.evidence.length}) - tap to view`)}
                    </summary>
                    <div className="space-y-4 px-5 pb-5 text-sm">
                      {a.evidence.length > 0 && (
                        <ol className="space-y-2">
                          {a.evidence.map((s, i) => (
                            <li key={`${s.url}-${i}`} className="flex gap-2">
                              <span className="text-muted-foreground">{i + 1}.</span>
                              <a
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                                className="break-words text-primary underline-offset-4 hover:underline"
                              >
                                {s.title || s.url}
                              </a>
                            </li>
                          ))}
                        </ol>
                      )}
                      {a.inspiredBy.length > 0 && (
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">
                            {ui("Inspired by")}
                          </div>
                          <ul className="mt-2 space-y-1">
                            {a.inspiredBy.map((s, i) => (
                              <li key={`${s.url}-insp-${i}`}>
                                <a
                                  href={s.url}
                                  target="_blank"
                                  rel="noopener noreferrer nofollow"
                                  className="break-words text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                                >
                                  {s.title && !/^(undefined|null)$/i.test(s.title)
                                    ? s.title
                                    : ui("Original Smartasaurus reel")}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </details>
                );
              })()}

              {/* Ego first - your record, then one single action row. */}
              {answered && (
                <CallRecordBanner
                  correct={streak.correct}
                  total={streak.total}
                  streak={streak.streak}
                  bestStreak={streak.bestStreak}
                  recent={streak.recent}
                  url={url}
                />
              )}

              {/* Sharing/saving lives in the action bar directly under the video. */}

              <ContextualProductCTA
                categorySlug={category?.slug ?? null}
                articleSlug={discovery.slug}
              />

              {/* SINGLE primary next-action.
              In a series → Up Next (the only nudge - no BlewYourMind noise).
              Outside a series → BlewYourMind 3-card engine.
              This is the biggest simplification: one clear decision per page. */}
              {collectionContext && collNext ? (
                <UpNextInCollection ctx={collectionContext} next={collNext} visited={visited} />
              ) : null}
              {collectionContext && collNext && (
                <AutoNextCountdown
                  currentSlug={discovery.slug}
                  nextSlug={collNext.slug}
                  nextTitle={collNext.title}
                  nextImage={collNext.image_url ?? null}
                  blocked={foreignActive || foreignPlaying}
                />
              )}
              {!(collectionContext && collNext) &&
                (collectionContext &&
                collectionContext.currentIndex === collectionContext.items.length - 1 ? (
                  <UpNextInCollection ctx={collectionContext} next={null} visited={visited} />
                ) : (
                  <BlewYourMind
                    currentSlug={discovery.slug}
                    related={related}
                    crossCategory={crossCategory}
                    trending={trending}
                    collectionContext={collectionContext}
                    collNextSlug={collNext?.slug ?? null}
                    category={category}
                    categories={categories}
                  />
                ))}
            </div>

            {/* Sticky right rail - series-at-a-glance on lg+. Narration
                is owned by the cinematic hero above (single playback engine). */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 space-y-4">
                {collectionContext && <SeriesRail ctx={collectionContext} visited={visited} />}
              </div>
            </aside>
          </div>
          </>}
        </article>

        {/* Mobile-only next-article surface. Uses the series' next episode when
            available, else the top recommendation, so retention is nudged even
            before the reader reaches the end-of-article grid. */}
        {!awaitingTranslation && (() => {
          const nextCandidate = pickUnseen<{ slug: string; title: string }>(
            [
              collNext,
              ...related,
              ...crossCategory,
              ...trending.map((t) => ({ slug: t.slug, title: t.title })),
            ],
            sessionVisited,
            discovery.slug,
          );
          if (!nextCandidate) return null;

          return (
            <StickyNextBar
              nextSlug={nextCandidate.slug}
              nextTitle={nextCandidate.title}
              articleSlug={discovery.slug}
            />
          );
        })()}
      </main>
      <SiteFooter />
    </div>
  );
}

function CollectionStrip({ ctx, visited }: { ctx: CollectionContext; visited: Set<string> }) {
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const total = ctx.items.length;
  const pos = ctx.currentIndex + 1;
  const prev = ctx.currentIndex > 0 ? ctx.items[ctx.currentIndex - 1] : null;
  const next = ctx.currentIndex < total - 1 ? ctx.items[ctx.currentIndex + 1] : null;
  const journey = getJourney(ctx.collection.slug);
  const journeySearch = journey ? ({ from: ctx.collection.slug } as const) : undefined;
  // Progress = number of items in this collection the reader has actually
  // visited. We do NOT credit the current position - otherwise landing on
  // Episode 20 directly would falsely trigger "Season Complete".
  const visitedCount = ctx.items.filter((i) => visited.has(i.slug)).length;
  const pct = total > 0 ? Math.round((visitedCount / total) * 100) : 0;
  const complete = total > 0 && ctx.items.every((i) => visited.has(i.slug));

  return (
    <div className="border-b border-border bg-card/60">
      <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-3 md:gap-3 md:px-8 md:py-4">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 md:flex md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
              {complete ? ui("Season Complete") : (journey?.eyebrow ?? ui("Series"))}
            </div>
            <Link
              to="/collections/$slug"
              params={{ slug: ctx.collection.slug }}
              className="mt-0.5 block truncate font-display text-base uppercase text-foreground hover:text-primary md:text-lg"
              title={ctx.collection.title}
            >
              {ctx.collection.title}
            </Link>
            {complete ? (
              <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {ui("Season Complete")} ✓ · {ui(`Episode ${pos} of ${total}`)}
              </div>
            ) : (
              <>
                <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {ui(`Episode ${pos} of ${total}`)}
                </div>
                <div className="mt-1 hidden text-[11px] font-semibold uppercase tracking-wider text-muted-foreground md:block">
                  {ui(`${visitedCount} of ${total} read`)}
                </div>
                <div className="mt-1.5 hidden h-1 w-full max-w-md overflow-hidden rounded-full bg-border md:block md:w-72">
                  <div
                    className="h-full bg-gradient-curiosity transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            {prev && (
              <Link
                to="/discovery/$slug"
                params={{ slug: prev.slug }}
                search={journeySearch}
                onClick={() => trackConversion("next_click", `/discovery/${prev.slug}`)}
                aria-label={ui("Previous episode")}
                title={ui("Previous episode")}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background/40 text-foreground hover:border-primary/50 hover:text-primary md:h-auto md:w-auto md:gap-1.5 md:px-3 md:py-1.5"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> <span className="hidden text-[11px] font-bold uppercase tracking-wider md:inline">{ui("Previous")}</span>
              </Link>
            )}
            {next ? (
              <Link
                to="/discovery/$slug"
                params={{ slug: next.slug }}
                search={journeySearch}
                onClick={() => trackConversion("next_click", `/discovery/${next.slug}`)}
                aria-label={ui("Next Episode")}
                title={ui("Next Episode")}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground hover:brightness-110 md:h-auto md:w-auto md:gap-1.5 md:px-3 md:py-1.5"
              >
                <span className="hidden text-[11px] font-bold uppercase tracking-wider md:inline">{ui("Next Episode")}</span> <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : journey && journey.next ? (
              <Link
                to="/collections/$slug"
                params={{ slug: journey.next.slug }}
                onClick={() => trackConversion("next_click", `/collections/${journey.next!.slug}`)}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-primary-foreground hover:brightness-110"
              >
                {ui("Next Series")} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : (
              <Link
                to="/collections"
                onClick={() => trackConversion("next_click", "/collections")}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-primary-foreground hover:brightness-110"
              >
                {ui("Pick Another Series")} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        </div>

        {/* Episode rail - Netflix-style numbered dots so readers see the whole series at a glance. */}
        <div className="-mx-2 hidden gap-1.5 overflow-x-auto px-2 pb-1 [scrollbar-width:none] md:flex [&::-webkit-scrollbar]:hidden">
          {ctx.items.map((item, i) => {
            const isCurrent = i === ctx.currentIndex;
            const isVisited = visited.has(item.slug);
            const base =
              "flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full px-2 text-[10px] font-bold uppercase tracking-wider transition";
            const cls = isCurrent
              ? `${base} bg-primary text-primary-foreground ring-2 ring-primary/40`
              : isVisited
                ? `${base} border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20`
                : `${base} border border-border bg-background/40 text-muted-foreground hover:border-primary/50 hover:text-primary`;
            return (
              <Link
                key={item.id}
                to="/discovery/$slug"
                params={{ slug: item.slug }}
                search={journeySearch}
                title={`${i + 1}. ${item.title}`}
                className={cls}
                aria-current={isCurrent ? "true" : undefined}
              >
                {isVisited && !isCurrent ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  String(i + 1).padStart(2, "0")
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Compact series companion shown in the sticky right rail on lg+ screens.
 * Mirrors the CollectionStrip's numbered dot rail so the reader always sees
 * where they are within the series while reading. Not shown on mobile - the
 * top CollectionStrip already carries that job on smaller viewports.
 */
function SeriesRail({ ctx, visited }: { ctx: CollectionContext; visited: Set<string> }) {
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const total = ctx.items.length;
  const pos = ctx.currentIndex + 1;
  const journey = getJourney(ctx.collection.slug);
  const journeySearch = journey ? ({ from: ctx.collection.slug } as const) : undefined;
  const next = ctx.currentIndex < total - 1 ? ctx.items[ctx.currentIndex + 1] : null;
  const visitedCount = ctx.items.filter((i) => visited.has(i.slug)).length;
  const pct = total > 0 ? Math.round((visitedCount / total) * 100) : 0;
  return (
    <div className="rounded-2xl border border-border bg-card/60 p-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
        {journey?.eyebrow ?? ui("Series")}
      </div>
      <Link
        to="/collections/$slug"
        params={{ slug: ctx.collection.slug }}
        className="mt-1 block truncate font-display text-sm uppercase text-foreground hover:text-primary"
        title={ctx.collection.title}
      >
        {ctx.collection.title}
      </Link>
      <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {ui(`Episode ${pos} of ${total}`)}
      </div>
      <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-border">
        <div className="h-full bg-gradient-curiosity transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {ctx.items.map((item, i) => {
          const isCurrent = i === ctx.currentIndex;
          const isVisited = visited.has(item.slug);
          const base =
            "flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-[9px] font-bold uppercase tracking-wider transition";
          const cls = isCurrent
            ? `${base} bg-primary text-primary-foreground`
            : isVisited
              ? `${base} border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20`
              : `${base} border border-border bg-background/40 text-muted-foreground hover:border-primary/50 hover:text-primary`;
          return (
            <Link
              key={item.id}
              to="/discovery/$slug"
              params={{ slug: item.slug }}
              search={journeySearch}
              title={`${i + 1}. ${item.title}`}
              className={cls}
              aria-current={isCurrent ? "true" : undefined}
            >
              {isVisited && !isCurrent ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                String(i + 1).padStart(2, "0")
              )}
            </Link>
          );
        })}
      </div>
      {next && (
        <Link
          to="/discovery/$slug"
          params={{ slug: next.slug }}
          search={journeySearch}
          className="mt-4 flex items-center justify-between gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-primary transition hover:bg-primary/10"
        >
          <span className="truncate">{ui("Next")}: {next.title}</span>
          <ArrowRight className="h-3.5 w-3.5 shrink-0" />
        </Link>
      )}
    </div>
  );
}

/**
 * End-of-article binge nudge. The single most important "one more discovery"
 * trigger - appears the moment the reader finishes the body, before any
 * generic prev/next or related content can pull them away.
 */
function UpNextInCollection({
  ctx,
  next,
  visited,
}: {
  ctx: CollectionContext;
  next: { id: string; slug: string; title: string; image_url: string | null } | null;
  visited: Set<string>;
}) {
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const total = ctx.items.length;
  const visitedCount = ctx.items.filter((i) => visited.has(i.slug)).length;
  // Series progression, not personal reading history: "Episode 52 of 54" must
  // read "2 episodes left", never "54 episodes left" for a first-time visitor.
  const remaining = Math.max(0, total - (ctx.currentIndex + 1));
  const journey = getJourney(ctx.collection.slug);
  const journeySearch = journey ? ({ from: ctx.collection.slug } as const) : undefined;
  const complete = total > 0 && ctx.items.every((i) => visited.has(i.slug));
  const firstUnread = ctx.items.find((i) => !visited.has(i.slug)) ?? null;

  if (!next) {
    // On the last episode of the series.
    // Only celebrate when the reader has actually read every episode -
    // otherwise nudge them back to the first unread episode.
    if (!complete && firstUnread) {
      return (
        <section className="mt-8 overflow-hidden rounded-2xl border border-primary/30 bg-card p-6 md:p-8">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
            {ui("Almost there")}
          </div>
          <h2 className="mt-2 font-display text-2xl uppercase leading-tight md:text-3xl">
            {remaining} episode{remaining === 1 ? "" : "s"} left in “{ctx.collection.title}”.
          </h2>
          <p className="mt-2 text-sm text-muted-foreground md:text-base">
            You skipped ahead. Read the earlier episodes to unlock the full series.
          </p>
          <Link
            to="/discovery/$slug"
            params={{ slug: firstUnread.slug }}
            search={journeySearch}
            className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-primary-foreground hover:brightness-110"
          >
            Go to first unread episode <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </section>
      );
    }
    // Truly complete - celebrate.
    return (
      <section className="mt-8 overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-6 md:p-8">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
          <CheckCircle2 className="h-4 w-4" /> {ui("Season Complete")}
        </div>
        <h2 className="mt-2 font-display text-2xl uppercase leading-tight md:text-3xl">
          You finished “{ctx.collection.title}”.
        </h2>
        <p className="mt-2 text-sm text-muted-foreground md:text-base">
          {visitedCount} of {total} episodes read.
          {journey?.next ? ` ${journey.next.pitch}` : " Ready for the next rabbit hole?"}
        </p>
        {journey?.next ? (
          <Link
            to="/collections/$slug"
            params={{ slug: journey.next.slug }}
            className="plausible-event-name=Season+Complete+Next mt-5 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-primary-foreground hover:brightness-110"
          >
            {ui("Next Series")} · {journey.next.title} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        ) : (
          <Link
            to="/collections"
            className="plausible-event-name=Season+Complete+Browse mt-5 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-primary-foreground hover:brightness-110"
          >
            {ui("Browse Series")} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </section>
    );
  }

  const img = sizedImage(next.image_url, 640);
  return (
    <Link
      to="/discovery/$slug"
      params={{ slug: next.slug }}
      search={journeySearch}
      onClick={() => trackConversion("next_click", `/discovery/${next.slug}`)}
      className="plausible-event-name=Up+Next+Click group mt-8 block overflow-hidden rounded-2xl border border-primary/30 bg-card transition hover:border-primary/60 hover:glow-fire"
    >
      <div className="grid grid-cols-1 md:grid-cols-[40%_1fr]">
        <div className="relative aspect-[16/10] overflow-hidden md:aspect-auto">
          {img ? (
            <img
              src={img}
              alt={next.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="h-full w-full bg-gradient-curiosity opacity-50" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent md:bg-gradient-to-r" />
          <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-foreground">
            <PlayCircle className="h-3 w-3" /> {ui(`Up Next · Episode ${ctx.currentIndex + 2}`)}
          </div>
        </div>
        <div className="flex flex-col justify-center p-5 md:p-7">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
            {ui(`Continue Series: ${ctx.collection.title}`)}
          </div>
          <h3 className="mt-2 font-display text-2xl uppercase leading-tight text-foreground transition group-hover:text-primary md:text-3xl">
            {next.title}
          </h3>
          <div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {ui(`${remaining} episode${remaining === 1 ? "" : "s"} left in this series`)}
          </div>
          <span className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-primary-foreground transition group-hover:brightness-110">
            {ui("Play Next Episode")} <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

/**
 * "If this blew your mind…" - 3-card recommendation engine.
 *
 * Priority order:
 *   1. Same collection (current article's collection siblings)
 *   2. Same curiosity archetype (categories sharing an ARCHETYPE affinity group)
 *   3. Same category (token-ranked siblings)
 *   4. Trending fallback
 *
 * Never recommends the current article or the article already shown in the
 * "Up Next" episode card. De-prioritizes anything the reader has opened in
 * the current session (tracked via sessionStorage). Falls back gracefully
 * when any tier is empty.
 */
function BlewYourMind({
  currentSlug,
  related,
  crossCategory,
  trending,
  collectionContext,
  collNextSlug,
  category,
  categories,
}: {
  currentSlug: string;
  related: DBDiscovery[];
  crossCategory: DBDiscovery[];
  trending: TrendingItem[];
  collectionContext: CollectionContext | null;
  collNextSlug: string | null;
  category: DBCategory | null;
  categories: DBCategory[];
}) {
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const sessionVisited = useSessionVisited(currentSlug);
  // The article the reader most likely just came from - never offer it back.
  const previousSlug = useMemo(() => {
    const arr = readSessionVisited().filter((s) => s !== currentSlug);
    return arr.length ? arr[arr.length - 1] : null;
  }, [currentSlug]);

  const peerCategorySlugs = useMemo(() => {
    const peers = new Set<string>();
    if (!category) return peers;
    for (const a of Object.values(ARCHETYPES)) {
      if (a.categoryAffinity.includes(category.slug)) {
        for (const s of a.categoryAffinity) peers.add(s);
      }
    }
    peers.delete(category.slug); // archetype peers means OTHER categories in the group
    return peers;
  }, [category]);

  const slugById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categories) m.set(c.id, c.slug);
    if (category) m.set(category.id, category.slug);
    return m;
  }, [categories, category]);

  type Pick = {
    slug: string;
    title: string;
    image_url: string | null;
    excerpt: string | null;
    tag: string;
  };
  const picks: Pick[] = [];
  const used = new Set<string>([currentSlug]);
  if (collNextSlug) used.add(collNextSlug);
  // Hard block: the article the reader arrived from is never a valid next step,
  // not even in the last-resort tier. This is what created A -> B -> A loops.
  if (previousSlug) used.add(previousSlug);

  const addPick = (
    d:
      | { slug: string; title: string; image_url: string | null; excerpt?: string | null }
      | null
      | undefined,
    tag: string,
    opts: { allowSessionVisited?: boolean } = {},
  ) => {
    if (!d || used.has(d.slug)) return;
    if (!opts.allowSessionVisited && sessionVisited.has(d.slug)) return;
    used.add(d.slug);
    picks.push({
      slug: d.slug,
      title: d.title,
      image_url: d.image_url,
      excerpt: d.excerpt ?? null,
      tag,
    });
  };

  // 1. Same collection siblings (skip current and the Up-Next episode card).
  if (collectionContext) {
    const start = Math.max(0, collectionContext.currentIndex);
    const ordered = [
      ...collectionContext.items.slice(start + 1),
      ...collectionContext.items.slice(0, start),
    ];
    for (const item of ordered) {
      if (picks.length >= 3) break;
      addPick(item, "Same collection");
    }
  }

  // 2. Same curiosity archetype (categories sharing an affinity group).
  for (const d of crossCategory) {
    if (picks.length >= 3) break;
    const catSlug = slugById.get(d.category_id ?? "");
    if (catSlug && peerCategorySlugs.has(catSlug)) addPick(d, "Same vibe");
  }

  // 3. Same category.
  for (const d of related) {
    if (picks.length >= 3) break;
    addPick(d, category ? `More ${category.label}` : "Related");
  }

  // 4. High-performing fallback.
  for (const t of trending) {
    if (picks.length >= 3) break;
    addPick({ slug: t.slug, title: t.title, image_url: t.image_url, excerpt: null }, "Trending");
  }

  // 5. Last resort - allow session-visited so we always render 3 cards if any data exists.
  if (picks.length < 3) {
    for (const d of [...related, ...crossCategory]) {
      if (picks.length >= 3) break;
      addPick(d, "Keep exploring", { allowSessionVisited: true });
    }
    if (collectionContext) {
      for (const item of collectionContext.items) {
        if (picks.length >= 3) break;
        addPick(item, "Same collection", { allowSessionVisited: true });
      }
    }
  }

  if (picks.length === 0) return null;

  return (
    <section className="mt-8">
      <div className="mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
          · {ui("Just one more")}
        </div>
        <h2 className="mt-1 font-display text-2xl uppercase leading-tight md:text-3xl">
          {ui("If this blew your mind…")}
        </h2>
      </div>
      {/* ONE next step, not a grid. A single obvious card converts better than
          three competing ones, and article -> article is the only path that
          visitors actually take. */}
      <div className="grid grid-cols-1 gap-3">
        {picks.slice(0, 1).map((p) => {
          const img = sizedImage(p.image_url, 400);
          return (
            <Link
              key={p.slug}
              to="/discovery/$slug"
              params={{ slug: p.slug }}
              search={
                collectionContext &&
                getJourney(collectionContext.collection.slug) &&
                collectionContext.items.some((x) => x.slug === p.slug)
                  ? { from: collectionContext.collection.slug }
                  : undefined
              }
              onClick={() => {
                trackConversion("recommendation_accept", `/discovery/${p.slug}`);
                trackConversion("next_click", `/discovery/${p.slug}`);
              }}
              className="plausible-event-name=Blew+Your+Mind+Click group block overflow-hidden rounded-2xl border border-border bg-card transition hover:border-primary/60 hover:glow-fire"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                {img ? (
                  <img
                    src={img}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="h-full w-full bg-gradient-curiosity opacity-40" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute left-2.5 top-2.5 rounded-full bg-primary px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-primary-foreground">
                  {ui(p.tag)}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-display text-base uppercase leading-tight text-foreground transition group-hover:text-primary">
                  {p.title}
                </h3>
                {p.excerpt && (
                  <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{p.excerpt}</p>
                )}
                <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary">
                  {ui("Read")} <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
